import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import {
  Alert,
  Badge,
  Button,
  Card,
  Grid,
  Group,
  Modal,
  Skeleton,
  Stack,
  Tabs,
  Text,
  Textarea,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconArrowLeft, IconEdit, IconRefresh, IconWand } from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { notifications } from '@mantine/notifications';
import { useMemo, useState } from 'react';
import { trpc } from '../../lib/trpc';
import { TemplatePreview } from '../../components/TemplatePreview';

const STATUS_COLOR: Record<string, string> = {
  APPROVED: 'green',
  PENDING: 'yellow',
  REJECTED: 'red',
  PAUSED: 'orange',
  DISABLED: 'gray',
};
const QUALITY_COLOR: Record<string, string> = {
  GREEN: 'green',
  YELLOW: 'yellow',
  RED: 'red',
  UNKNOWN: 'gray',
};

function TemplateDetail() {
  const { name } = useParams({ from: '/vendor/templates/$name' });
  const q = trpc.whatsapp.templates.getByName.useQuery({ name });
  const utils = trpc.useUtils();
  const [activeLang, setActiveLang] = useState<string | null>(null);
  const [autoFixOpen, setAutoFixOpen] = useState(false);

  const sync = trpc.whatsapp.templates.syncFromMeta.useMutation({
    onSuccess: () => {
      void utils.whatsapp.templates.getByName.invalidate({ name });
      notifications.show({ message: 'Re-synced from Meta', color: 'green' });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const langs = q.data ?? [];
  const current = useMemo(
    () => (activeLang ? langs.find((l) => l.language === activeLang) : langs[0]),
    [langs, activeLang],
  );

  if (q.isPending) return <Skeleton height={400} />;
  if (q.isError || !current)
    return (
      <Stack>
        <Alert color="red">{q.error?.message ?? 'Template not found.'}</Alert>
        <Button component={Link} to="/vendor/templates" leftSection={<IconArrowLeft size={14} />}>
          Back to templates
        </Button>
      </Stack>
    );

  const buttons = parseButtons(current.buttonsJson);

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="md">
        <Group>
          <Button
            variant="subtle"
            component={Link}
            to="/vendor/templates"
            leftSection={<IconArrowLeft size={14} />}
          >
            Templates
          </Button>
        </Group>
        <Group justify="space-between" align="flex-start">
          <Stack gap={2}>
            <Title order={2}>{current.templateName}</Title>
            <Group gap={6}>
              <Badge variant="light">{current.category ?? '—'}</Badge>
              <Badge color={STATUS_COLOR[current.status ?? ''] ?? 'gray'} variant="light">
                {current.status}
              </Badge>
              <Tooltip label="Quality is set by Meta from message-feedback signals.">
                <Badge
                  color={QUALITY_COLOR[current.qualityScore ?? 'UNKNOWN'] ?? 'gray'}
                  variant="light"
                >
                  {current.qualityScore ?? 'UNKNOWN'}
                </Badge>
              </Tooltip>
            </Group>
          </Stack>
          <Group>
            <Button
              variant="default"
              leftSection={<IconRefresh size={14} />}
              loading={sync.isPending}
              onClick={() => sync.mutate()}
            >
              Re-sync
            </Button>
            {current.status === 'APPROVED' && (
              <Button
                renderRoot={(props) => (
                  <Link
                    to="/vendor/templates/$name/edit"
                    params={{ name: current.templateName }}
                    {...props}
                  />
                )}
                leftSection={<IconEdit size={14} />}
              >
                Edit
              </Button>
            )}
            {current.status === 'REJECTED' && (
              <Button
                color="orange"
                leftSection={<IconWand size={14} />}
                onClick={() => setAutoFixOpen(true)}
              >
                Auto-fix with AI
              </Button>
            )}
          </Group>
        </Group>

        {current.status === 'REJECTED' && current.rejectedReason && (
          <Alert color="red" title="Meta rejected this template">
            {current.rejectedReason}
          </Alert>
        )}

        {langs.length > 1 && (
          <Tabs value={current.language} onChange={(v) => setActiveLang(v)}>
            <Tabs.List>
              {langs.map((l) => (
                <Tabs.Tab key={l.language} value={l.language}>
                  {l.language}
                </Tabs.Tab>
              ))}
            </Tabs.List>
          </Tabs>
        )}

        <Grid gap="md">
          <Grid.Col span={{ base: 12, md: 7 }}>
            <Card>
              <Stack gap="sm">
                <Text size="sm" fw={500}>
                  Components
                </Text>
                {current.headerFormat && current.headerFormat !== 'NONE' && (
                  <Stack gap={2}>
                    <Text size="xs" c="dimmed">
                      Header
                    </Text>
                    <Text size="sm">
                      {current.headerFormat}
                      {current.headerText ? ` — ${current.headerText}` : ''}
                    </Text>
                  </Stack>
                )}
                <Stack gap={2}>
                  <Text size="xs" c="dimmed">
                    Body
                  </Text>
                  <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                    {current.bodyText ?? '(none)'}
                  </Text>
                </Stack>
                {current.footerText && (
                  <Stack gap={2}>
                    <Text size="xs" c="dimmed">
                      Footer
                    </Text>
                    <Text size="sm">{current.footerText}</Text>
                  </Stack>
                )}
                {buttons.length > 0 && (
                  <Stack gap={2}>
                    <Text size="xs" c="dimmed">
                      Buttons
                    </Text>
                    {buttons.map((b, i) => (
                      <Text key={i} size="sm">
                        {b.type}
                        {b.text ? ` — ${b.text}` : ''}
                        {b.url ? ` → ${b.url}` : ''}
                        {b.phone_number ? ` ☎ ${b.phone_number}` : ''}
                      </Text>
                    ))}
                  </Stack>
                )}
              </Stack>
            </Card>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 5 }}>
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                Preview
              </Text>
              <TemplatePreview
                template={{
                  category:
                    (current.category as 'MARKETING' | 'UTILITY' | 'AUTHENTICATION') ?? 'MARKETING',
                  header: current.headerFormat
                    ? {
                        format: current.headerFormat as
                          | 'NONE'
                          | 'TEXT'
                          | 'IMAGE'
                          | 'VIDEO'
                          | 'GIF'
                          | 'DOCUMENT'
                          | 'LOCATION',
                        text: current.headerText ?? '',
                      }
                    : undefined,
                  bodyText: current.bodyText ?? '',
                  footerText: current.footerText ?? '',
                  buttons,
                }}
              />
            </Stack>
          </Grid.Col>
        </Grid>
      </Stack>

      <AutoFixDialog
        opened={autoFixOpen}
        onClose={() => setAutoFixOpen(false)}
        bodyText={current.bodyText ?? ''}
        rejectedReason={current.rejectedReason ?? ''}
      />
    </motion.div>
  );
}

function AutoFixDialog({
  opened,
  onClose,
  bodyText,
  rejectedReason,
}: {
  opened: boolean;
  onClose: () => void;
  bodyText: string;
  rejectedReason: string;
}) {
  const [reason, setReason] = useState(rejectedReason);
  const [suggested, setSuggested] = useState<string | null>(null);
  const fix = trpc.bots.ai.autoFixTemplate.useMutation({
    onSuccess: (r) => {
      setSuggested(r.bodyText ?? null);
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  return (
    <Modal opened={opened} onClose={onClose} title="Auto-fix with AI" size="lg" centered>
      <Stack>
        <Textarea
          label="Reject reason from Meta (you can edit)"
          autosize
          minRows={2}
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
        />
        <Textarea label="Current body" autosize minRows={3} value={bodyText} readOnly />
        <Group>
          <Button loading={fix.isPending} onClick={() => fix.mutate({ bodyText, reason })}>
            Suggest fix
          </Button>
        </Group>
        {suggested !== null && (
          <Card withBorder>
            <Text size="sm" fw={500} mb={4}>
              Suggested body
            </Text>
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
              {suggested}
            </Text>
            <Text size="xs" c="dimmed" mt={4}>
              Copy this into a new draft via "New template" — Meta won't accept an edit of a
              REJECTED template, you must submit a new one (try `_v2` suffix).
            </Text>
          </Card>
        )}
      </Stack>
    </Modal>
  );
}

function parseButtons(json: string | null | undefined): Array<{
  type: string;
  text?: string;
  url?: string;
  phone_number?: string;
  copyCodeExample?: string;
}> {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    if (!Array.isArray(parsed)) return [];
    return parsed as Array<{
      type: string;
      text?: string;
      url?: string;
      phone_number?: string;
      copyCodeExample?: string;
    }>;
  } catch {
    return [];
  }
}

export const Route = createFileRoute('/vendor/templates/$name')({ component: TemplateDetail });
